import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Alert,
  ProgressBar,
  Cards,
  ColumnLayout,
  Badge,
  TextContent,
} from '@cloudscape-design/components';
import { useDemoContext } from '../context/DemoContext';
import apiService from '../services/api';

function UploadDocumentsPage() {
  const navigate = useNavigate();
  const { selectedFile: contextSelectedFile, resetDemo, setManualAnalysisResults } = useDemoContext();
  const [selectedFile, setSelectedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize selectedFile from context when component mounts
  useEffect(() => {
    if (contextSelectedFile) {
      setSelectedFile(contextSelectedFile);
    }
  }, [contextSelectedFile]);

  // Fetch available files from API
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setLoading(true);
        const files = await apiService.getAvailableFiles();
        setAvailableFiles(files);
        setError(null);
      } catch (err) {
        setError('Failed to load available files. Please try again later.');
        console.error('Error loading files:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const handleAnalyze = () => {
    if (!selectedFile) return;
    
    setProcessing(true);
    
    // Update the selected file in the context
    setManualAnalysisResults(selectedFile, {}, 0);
    
    // Simulate processing progress
    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 10;
      setProgress(currentProgress);
      
      if (currentProgress >= 100) {
        clearInterval(interval);
        setProcessing(false);
        // Navigate to manual analysis page
        navigate('/manual-analysis');
      }
    }, 500);
  };

  // Handle card selection
  const handleSelectionChange = ({ detail }) => {
    const newSelectedFile = detail.selectedItems[0] || null;
    setSelectedFile(newSelectedFile);
    
    // Also update the context with the selected file
    if (newSelectedFile) {
      setManualAnalysisResults(newSelectedFile, {}, 0);
    }
  };

  const handleReset = () => {
    resetDemo();
    navigate('/');
  };

  // Custom component for persona information
  const PersonaSection = ({ persona }) => {
    if (!persona) return null;
    
    return (
      <Box padding="s">
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <img 
              src={persona.avatar} 
              alt={`${persona.name}'s avatar`} 
              style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #eaeded'
              }} 
            />
          </div>
          <div>
            <TextContent>
              <h4 style={{ margin: '0 0 5px 0' }}>{persona.name}</h4>
              <div><Badge>{persona.jobTitle}</Badge></div>
              <div style={{ fontSize: '12px', marginTop: '5px' }}><strong>Function:</strong> {persona.jobFunction}</div>
              <div style={{ fontSize: '12px' }}><strong>Task:</strong> {persona.task}</div>
            </TextContent>
          </div>
        </ColumnLayout>
        
        <Box padding={{ top: 's' }}>
          <TextContent>
            <h5 style={{ margin: '10px 0 5px 0', color: '#545b64' }}>Current Process</h5>
            <div style={{ fontSize: '13px', color: '#545b64' }}>
              {persona.currentProcess}
            </div>
            
            <h5 style={{ margin: '15px 0 5px 0', color: '#16191f' }}>AI Opportunity</h5>
            <div style={{ fontSize: '13px', color: '#16191f', fontWeight: '500' }}>
              {persona.aiOpportunity}
            </div>
          </TextContent>
        </Box>
      </Box>
    );
  };

  return (
    <Container>
      <SpaceBetween size="l">
        <Header 
          variant="h1"
          actions={
            <Button iconName="refresh" onClick={handleReset}>Reset Demo</Button>
          }
        >
          Select Document
        </Header>
        
        <Alert>
          Select one of the available documents for analysis.
          Each document is associated with a financial professional persona who currently spends significant time analyzing these documents manually. 
          This demo showcases how AI can augment their work, enabling them to focus on higher-value tasks.
        </Alert>
        
        {loading ? (
          <Box textAlign="center" padding="l">
            <SpaceBetween size="m">
              <div>Loading available documents...</div>
              <ProgressBar label="Loading" indeterminate={true} />
            </SpaceBetween>
          </Box>
        ) : error ? (
          <Alert type="error">{error}</Alert>
        ) : (
          <Cards
            cardDefinition={{
              header: item => item.name || item.title,
              sections: [
                {
                  id: 'company',
                  header: 'Company',
                  content: item => item.company_name || item.company
                },
                {
                  id: 'persona',
                  header: 'Analyst Persona',
                  content: item => <PersonaSection persona={item.persona} />
                },
                {
                  id: 'type',
                  header: 'Type',
                  content: item => item.type || item.document_type_name
                },
                {
                  id: 'size',
                  header: 'Size',
                  content: item => item.size
                },
                {
                  id: 'description',
                  header: 'Description',
                  content: item => item.description
                }
              ]
            }}
            cardsPerRow={[
              { cards: 1 },
              { minWidth: 500, cards: 3 }
            ]}
            items={availableFiles}
            selectionType="single"
            entireCardClickable
            selectedItems={selectedFile ? [selectedFile] : []}
            onSelectionChange={handleSelectionChange}
            trackBy="id"
            ariaLabels={{
              itemSelectionLabel: (e, item) => `Select ${item.name}`,
              selectionGroupLabel: "Document selection"
            }}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No documents available</b>
                <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                  There are no documents available for analysis.
                </Box>
              </Box>
            }
          />
        )}
        
        {processing && (
          <ProgressBar
            value={progress}
            label="Preparing document. Get ready..."
            description={`${progress}% complete`}
          />
        )}
        
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => navigate('/')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAnalyze}
              disabled={!selectedFile || processing || loading}
            >
              Start Manual Analysis
            </Button>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Container>
  );
}

export default UploadDocumentsPage;